import { useState, useRef, useEffect } from "react";
import { ChatMessage, ContactInfo, PersistedWebchatState } from "@/app/features/webchat/types";

export const useWebchatState = (storageKey: string) => {
  const pollingControlRef = useRef<{ shouldStop: boolean }>({ shouldStop: false });
  const lastActivityRef = useRef<number>(Date.now());

  // Helper function to load state from localStorage
  const loadPersistedState = (): Omit<PersistedWebchatState, 'timestamp'> & { messages: ChatMessage[] } | null => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved) as PersistedWebchatState;
        
        // Check if the saved state is too old (more than 24 hours)
        const twentyFourHours = 24 * 60 * 60 * 1000;
        const stateAge = Date.now() - new Date(parsed.timestamp || 0).getTime();
        
        if (stateAge > twentyFourHours) {
          localStorage.removeItem(storageKey);
          return null;
        }
        
        // Convert timestamp strings back to Date objects
        const messagesWithDates: ChatMessage[] = parsed.messages.map((msg) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
        
        // Update last activity if it exists
        if (parsed.lastActivity) {
          lastActivityRef.current = parsed.lastActivity;
        }
        
        return {
          messages: messagesWithDates as ChatMessage[],
          isChatStarted: parsed.isChatStarted,
          contactInfoSubmitted: parsed.contactInfoSubmitted,
          lastActivity: parsed.lastActivity
        } as any;
      }
    } catch (error) {
      console.warn("Failed to load persisted webchat state:", error);
    }
    return null;
  };

  // Helper function to save state to localStorage
  const savePersistedState = (state: Omit<PersistedWebchatState, 'timestamp'> & { timestamp: string }) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (error) {
      console.warn("Failed to save webchat state:", error);
    }
  };

  const clearPersistedState = () => {
    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.warn("Failed to clear persisted webchat state:", error);
    }
  };

  // Initialize state with persisted data or defaults
  const persistedState = loadPersistedState();
  // Always force fresh contact selection on page load - don't persist contact info
  const [contactInfo, setContactInfo] = useState<ContactInfo>({ email: "", phone: "", subChannel: "" });
  const [currentMessage, setCurrentMessage] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>(persistedState?.messages || []);
  const [isChatStarted, setIsChatStarted] = useState(persistedState?.isChatStarted || false);
  const [contactInfoSubmitted, setContactInfoSubmitted] = useState(persistedState?.contactInfoSubmitted || false);
  const [hasSessionError, setHasSessionError] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);

  // Persist state to localStorage whenever key state changes (excluding contactInfo)
  useEffect(() => {
    const stateToSave = {
      // Don't persist contactInfo - force fresh selection on each page load
      messages: messages.map(msg => ({
        ...msg,
        timestamp: msg.timestamp.toISOString()
      })),
      isChatStarted,
      contactInfoSubmitted,
      timestamp: new Date().toISOString(),
      lastActivity: lastActivityRef.current
    };
    savePersistedState(stateToSave as any);
  }, [messages, isChatStarted, contactInfoSubmitted, storageKey]);

  const resetChatState = () => {
    setContactInfo({ email: "", phone: "", subChannel: "" });
    setMessages([]);
    setIsChatStarted(false);
    setContactInfoSubmitted(false);
    setCurrentMessage("");
    setHasSessionError(false);
    clearPersistedState();
  };

  return {
    contactInfo,
    setContactInfo,
    currentMessage,
    setCurrentMessage,
    messages,
    setMessages,
    isChatStarted,
    setIsChatStarted,
    contactInfoSubmitted,
    setContactInfoSubmitted,
    hasSessionError,
    setHasSessionError,
    isSendingMessage,
    setIsSendingMessage,
    pollingControlRef,
    lastActivityRef,
    resetChatState,
    clearPersistedState
  };
};